import type { MultiplierAddedInput } from "@slot/client-engine";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import type { SavedTotalTuning } from "../../tuning/savedTotalTuning";
import type { VsWildTopLabelEntrance } from "../vs-battle/VsWildLayer";
import type { SavedTotalView } from "./SavedTotalView";

/**
 * The Super Bonus saved total (Cocos `PersistentMultiplierPanelPresenter`, its store and `PersistentMultiplierFly`).
 * Server-authoritative: the panel shows `add_feature_multiplier`'s total as sent; the client computes no total.
 *
 *   feature enter, a session that keeps one → active, the panel still hidden (nothing is saved before a battle);
 *   add_feature_multiplier                  → the new total, shown (the session's first one shows the panel), a pulse;
 *   collect_feature_multiplier              → a stronger pulse (the last spin; the panel keeps its total);
 *   feature exit                            → inactive, hidden.
 *
 * Every Super Bonus battle after the first opens on the saved total (the backend's rule): while the panel shows that
 * value, it flies into the battle's top label and becomes it. Otherwise the label slides in as in any battle. Outside
 * an active session the actions are ignored, and logged.
 */
export class SavedTotalPresenter {
  private active = false;
  /** The total the panel shows: the last one the server sent this session (no suffix); null before the first. */
  private total: string | null = null;

  public constructor(private readonly input: { view: SavedTotalView | null; runner: PresentationTaskRunner; tuning: SavedTotalTuning; log: Log }) {}

  public onFeatureEnter(featureId: string): void {
    this.active = this.input.tuning.featureIds.includes(featureId);
    if (!this.active) this.input.log(`[SavedTotal] "${featureId}" keeps no saved total: no panel`);
  }

  public async presentAdded(added: MultiplierAddedInput): Promise<void> {
    if (!this.active) {
      this.input.log(`[SavedTotal] add (total ${added.totalMultiplier}) outside a session that keeps one: ignored`);
      return;
    }
    this.total = added.totalMultiplier;
    const view = this.input.view;
    if (!view) return;
    view.setValue(`${added.totalMultiplier}${this.input.tuning.suffix}`);
    view.show();
    await view.pulse(this.input.tuning.updatePulse, this.input.runner);
  }

  public async presentCollected(multiplierAdded: string, collectedSum: string): Promise<void> {
    if (!this.active) {
      this.input.log(`[SavedTotal] collect (${collectedSum}) outside a session that keeps one: ignored`);
      return;
    }
    this.input.log(`[SavedTotal] collect ${multiplierAdded} (sum ${collectedSum})`);
    await this.input.view?.pulse(this.input.tuning.collectPulse, this.input.runner);
  }

  public onFeatureExit(): void {
    this.active = false;
    this.total = null;
    this.input.view?.hide();
  }

  /**
   * The battle's top-label entrance: the shown total flies into it when it is that label's value. True once it has
   * landed (the label then shows in its place); false leaves the label to slide in.
   */
  public async flyIntoBattle(entrance: VsWildTopLabelEntrance): Promise<boolean> {
    const view = this.input.view;
    // Nothing shown: the session's first battle, or a battle outside a session that keeps a total.
    if (!view || this.total === null) return false;
    if (entrance.topValue !== this.total) {
      this.input.log(`[SavedTotal] the battle on reel ${entrance.column} opens on ${entrance.topValue}, the panel shows ${this.total}: no fly`);
      return false;
    }
    const { fly } = this.input.tuning;
    await view.flyValueTo({ target: entrance.target(), runner: this.input.runner, scope: entrance.scope, seconds: fly.seconds, arc: fly.arc, easing: fly.easing });
    this.input.log(`[SavedTotal] ${this.total} flew into the battle on reel ${entrance.column}`);
    return true;
  }
}
